class CartDonations extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');
    this.cartItems = document.getElementById('cart-items');
    this.checkoutButton = document.getElementById('gift-aid-submit');
    this.form = document.getElementById('cart');
    this.selectedGiftAidValue = null;

    // Bind methods
    this.onDonationChange = this.onDonationChange.bind(this);
    this.onDialogCheckoutClick = this.onDialogCheckoutClick.bind(this);
    this.onDonationButtonClick = this.onDonationButtonClick.bind(this);
    this.onCustomDonationInput = this.onCustomDonationInput.bind(this);
    this.onCustomDonationFocus = this.onCustomDonationFocus.bind(this);
    this.onCustomDonationBlur = this.onCustomDonationBlur.bind(this);
    this.onCustomDonationKeypress = this.onCustomDonationKeypress.bind(this);
    this.handleCustomDonation = this.handleCustomDonation.bind(this);
    this.showDialog = this.showDialog.bind(this);
    this.closeDialog = this.closeDialog.bind(this);
    this.onCheckoutClick = this.onCheckoutClick.bind(this);
    // Event listeners
    this.addEventListener('donationChange', this.onDonationChange);
    this.addEventListener('checkoutClicked', this.onCheckoutClick);
    this.addEventListener(
      'change',
      debounce((event) => {
        this.onChange(event);
      }, ON_CHANGE_DEBOUNCE_TIMER),
    );

    // Attach button listeners
    this.attachButtonListeners();
  }

  unsubscribeCartUpdate = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      this.attachButtonListeners();
    });
  }

  attachButtonListeners() {
    const roundupBtn = this.querySelector('#donation-cart #roundup');
    if (roundupBtn) {
      roundupBtn.removeEventListener('click', this.onDonationButtonClick);
      roundupBtn.addEventListener('click', this.onDonationButtonClick);
    }

    // Add event listeners for the custom donation input
    const customDonationInput = this.querySelector('.custom-donation');
    if (customDonationInput) {
      customDonationInput.removeEventListener('input', this.onCustomDonationInput);
      customDonationInput.addEventListener('input', this.onCustomDonationInput);

      customDonationInput.removeEventListener('focus', this.onCustomDonationFocus);
      customDonationInput.addEventListener('focus', this.onCustomDonationFocus);

      customDonationInput.removeEventListener('blur', this.onCustomDonationBlur);
      customDonationInput.addEventListener('blur', this.onCustomDonationBlur);

      customDonationInput.removeEventListener('keypress', this.onCustomDonationKeypress);
      customDonationInput.addEventListener('keypress', this.onCustomDonationKeypress);
    }

    const organisationSubmitBtn = this.querySelector('#organisation-submit');
    if (organisationSubmitBtn) {
      organisationSubmitBtn.removeEventListener('click', this.onDialogCheckoutClick);
      organisationSubmitBtn.addEventListener('click', this.onDialogCheckoutClick);
    }

    const customCheckoutButton = this.querySelector('custom-checkout-button');
    if (customCheckoutButton) {
      customCheckoutButton.removeEventListener('checkoutClicked', this.onCheckoutClick);
      customCheckoutButton.addEventListener('checkoutClicked', this.onCheckoutClick);
    }

    const giftAidBtns = this.querySelectorAll('.gift-aid-button');
    giftAidBtns.forEach((btn) => {
      btn.removeEventListener('click', this.onDialogCheckoutClick);
      btn.addEventListener('click', this.onDialogCheckoutClick);
    });

    const dialogEl = this.querySelector('dialog#favDialog') || document.getElementById('favDialog');
    if (dialogEl) {
      dialogEl.addEventListener('click', (event) => {
        if (event.target === dialogEl) {
          dialogEl.close();
        }
      });

      const closeDialogBtn = dialogEl.querySelector('button[value="cancel"]');
      if (closeDialogBtn) {
        closeDialogBtn.addEventListener('click', this.closeDialog);
      }
    } else {
      console.warn('Dialog element not found in the DOM');
    }
  }

  // Handle custom donation input formatting
  onCustomDonationInput(e) {
    try {
      const input = e.target;
      // Remove non-numeric and non-decimal characters except the first decimal point
      let value = input.value.replace(/[^\d.]/g, '');

      // Only allow one decimal point
      const decimalPoints = value.match(/\./g);
      if (decimalPoints && decimalPoints.length > 1) {
        value = value.substring(0, value.lastIndexOf('.'));
      }

      // Limit to two decimal places
      if (value.includes('.')) {
        const parts = value.split('.');
        if (parts[1].length > 2) {
          parts[1] = parts[1].substring(0, 2);
          value = parts.join('.');
        }
      }

      // Update the input value
      input.value = value;

      const amount = parseFloat(value);
      if (amount && amount > 0) {
        // Store the custom donation amount as a data attribute for processing
        input.dataset.rounded = amount;
        // Create a custom ID for tracking
        input.id = 'custom-donation-' + amount.toFixed(2);
      }
    } catch (err) {
      console.error('Error in custom donation input handler:', err);
    }
  }

  // Clear other donation buttons when focused
  onCustomDonationFocus(e) {
    try {
      // Remove active class from donation buttons
      this.querySelectorAll('.roundup').forEach((button) => {
        if (button) {
          button.classList.remove('active');
        }
      });
    } catch (err) {
      console.error('Error in focus handler:', err);
    }
  }

  // Handle Enter key press
  onCustomDonationKeypress(e) {
    try {
      if (e.key === 'Enter') {
        e.preventDefault();
        const amount = parseFloat(e.target.value);
        if (amount && amount > 0) {
          // Format to show pence
          e.target.value = amount.toFixed(2);

          // Process the donation
          this.handleCustomDonation(amount, e.target);
        }
      }
    } catch (err) {
      console.error('Error in keypress handler:', err);
      if (e.target) e.target.disabled = false;
    }
  }

  // Process donation when input loses focus
  onCustomDonationBlur(e) {
    try {
      const amount = parseFloat(e.target.value);
      if (amount && amount > 0) {
        // Format to show pence
        e.target.value = amount.toFixed(2);

        // Don't trigger the donation if enter was pressed (it's already handled)
        if (!e.target.disabled) {
          // Process the donation
          this.handleCustomDonation(amount, e.target);
        }
      } else if (e.target.value !== '') {
        // If invalid value, clear the input
        e.target.value = '';
      }
    } catch (err) {
      console.error('Error in blur handler:', err);
      if (e.target) e.target.disabled = false;
    }
  }

  // Handle custom donation processing
  async handleCustomDonation(amount, element) {
    try {
      if (!amount || amount <= 0) return;

      element.disabled = true;
      const cartItems = document.querySelector('cart-items') || document.querySelector('cart-drawer-items');

      if (cartItems && typeof cartItems.addDonationToCart === 'function') {
        try {
          await cartItems.addDonationToCart(amount);
          element.disabled = false;
          element.classList.add('active');
        } catch (err) {
          console.error('Error adding donation using cart method:', err);
          this.fallbackDonationHandler(amount, element);
        }
      } else {
        this.fallbackDonationHandler(amount, element);
      }
    } catch (err) {
      console.error('Error in handleCustomDonation:', err);
      if (element) element.disabled = false;
    }
  }

  // Fallback method if normal donation process fails
  async fallbackDonationHandler(amount, element) {
    try {
      console.log('Using fallback donation method for amount:', amount);
      // Get donation variant ID from button that matches closest amount
      let variantId = null;
      const donationButtons = this.querySelectorAll('.roundup[id]');

      // Check if any donation button has an ID we can use
      if (donationButtons.length > 0) {
        // Find the button with the amount closest to our custom amount
        let closestButton = null;
        let closestDiff = Infinity;

        donationButtons.forEach((button) => {
          if (button && button.getAttribute('data-rounded')) {
            const buttonAmount = parseFloat(button.getAttribute('data-rounded'));
            if (!isNaN(buttonAmount)) {
              const diff = Math.abs(buttonAmount - amount);
              if (diff < closestDiff) {
                closestDiff = diff;
                closestButton = button;
              }
            }
          }
        });

        // Use the ID from the closest button
        if (closestButton && closestButton.id) {
          variantId = closestButton.id;
        }
      }

      if (variantId) {
        // Add donation to cart using fetch
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            items: [
              {
                quantity: 1,
                id: variantId,
                properties: {
                  'Custom Amount': '£' + amount.toFixed(2),
                },
              },
            ],
          }),
        });

        const data = await response.json();

        // Update cart UI
        if (window.refreshCart) {
          window.refreshCart();
        } else {
          // If we can't find a refresh function, trigger a cart update event
          const event = new CustomEvent('cart:refresh', {
            bubbles: true,
          });
          document.dispatchEvent(event);
        }

        element.disabled = false;
        element.classList.add('active');
      } else {
        console.error('No donation variant ID found for fallback');
        element.disabled = false;
      }
    } catch (err) {
      console.error('Error in fallback donation handler:', err);
      if (element) element.disabled = false;
    }
  }

  async onDonationChange(event) {
    const buttonId = event.detail.id;
    // Use document.getElementById instead of querySelector to avoid CSS selector issues with numeric IDs
    const button = document.getElementById(buttonId);

    if (!button) {
      console.error('Button not found for ID:', buttonId);
      return;
    }

    const roundedValue = button.getAttribute('data-rounded');

    // Check if roundedValue is null, undefined, or empty
    if (!roundedValue || roundedValue === 'null' || roundedValue === 'undefined' || roundedValue.trim() === '') {
      console.error('Invalid rounded value:', roundedValue, 'for button:', buttonId);
      return;
    }

    // Remove active class from all buttons and add to clicked button
    this.querySelectorAll('.roundup, .custom-donation').forEach((btn) => {
      btn.classList.remove('active');
    });
    button.classList.add('active');

    const cartItems = document.querySelector('cart-items') || document.querySelector('cart-drawer-items');
    await cartItems.addDonationToCart(roundedValue);
  }

  async onDonationButtonClick(event) {
    event.preventDefault();

    // Find the actual button element (in case event.target is a child element like span)
    const button = event.target.closest('button');
    if (!button) {
      console.error('Button not found');
      return;
    }

    button.disabled = true;

    // Remove active class from all buttons and add to clicked button
    this.querySelectorAll('.roundup, .custom-donation').forEach((btn) => {
      btn.classList.remove('active');
    });
    button.classList.add('active');

    const roundedValue = button.getAttribute('data-rounded');

    // Check if roundedValue is null, undefined, or empty
    if (!roundedValue || roundedValue === 'null' || roundedValue === 'undefined' || roundedValue.trim() === '') {
      console.error('Invalid rounded value:', roundedValue);
      button.disabled = false;
      return;
    }

    const cartItems = document.querySelector('cart-items') || document.querySelector('cart-drawer-items');
    await cartItems.addDonationToCart(roundedValue);
    button.disabled = false;
  }

  onDialogCheckoutClick(event) {
    event.preventDefault();

    if (this.validateOrganisationFields()) {
      this.addFieldsToForm(event);
      const checkoutBtn = document.getElementById('checkout');
      // Clear org_ localStorage values before going to checkout
      localStorage.removeItem('org_id');
      localStorage.removeItem('org_name');
      localStorage.removeItem('org_type');
      localStorage.removeItem('org_free');
      checkoutBtn.click();
    }
  }

  addFieldsToForm(event) {
    // Bypass form field additions
    return;
    /*
    const customAttributesForm = document.getElementById('custom-attributes-form');
    const formData = new FormData(customAttributesForm);
    const selectedValue = event.target.getAttribute('data-value'); // 'Yes' or 'No'

    this.selectedGiftAidValue = selectedValue;

    let giftAidInput = this.form.querySelector('input[name="attributes[Gift aid]"]');
    if (!giftAidInput) {
      giftAidInput = document.createElement('input');
      giftAidInput.type = 'hidden';
      giftAidInput.name = 'attributes[Gift aid]';
      this.querySelector('#gift-aid-section').appendChild(giftAidInput);
    }
    giftAidInput.value = selectedValue;

    const consentInput = document.createElement('input');
    consentInput.type = 'hidden';
    consentInput.name = 'attributes[Consent]';
    consentInput.value = this.getConsentValue();
    const orgTypeInput = document.createElement('input');
    orgTypeInput.type = 'hidden';
    orgTypeInput.name = 'attributes[Organisation type]';
    orgTypeInput.value = this.getOrganisationType();
    this.form.appendChild(consentInput);
    this.form.appendChild(orgTypeInput);

    if (this.getOrganisationType() !== 'individual') {
      this.addOrganisationFields();
    }

    for (let [key, value] of formData.entries()) {
      console.log(`${key}: ${value}`);
    }
    */
  }

  getConsentValue() {
    const consentCheckbox = document.querySelector('input[name="attributes[Consent]"]');
    return consentCheckbox && consentCheckbox.checked ? 'Yes' : 'No';
  }

  getGiftAidValue() {
    const giftAidCheckbox = document.querySelector('input[name="attributes[Gift aid]"]');
    return giftAidCheckbox && giftAidCheckbox.checked ? 'Yes' : 'No';
  }

  getOrganisationType() {
    const orgTypeSelect = document.querySelector('select[name="attributes[Organisation type]"]');
    return orgTypeSelect ? orgTypeSelect.value : 'individual';
  }

  addOrganisationFields() {
    const form = document.getElementById('cart');
    const requiredFields = [
      { id: 'organisation-name', attributeName: 'attributes[Organisation name]' },
      { id: 'organisation-address-1', attributeName: 'attributes[Organisation street]' },
      { id: 'organisation-town', attributeName: 'attributes[Organisation town]' },
      { id: 'organisation-postcode', attributeName: 'attributes[Organisation postcode]' },
    ];

    const optionalFields = [{ id: 'organisation-address-2', attributeName: 'attributes[Organisation street two]' }];

    requiredFields.forEach((field) => {
      const element = this.querySelector(`#${field.id}`);
      if (element && element.value.trim() !== '') {
        if (!form.querySelector(`input[name="${field.attributeName}"]`)) {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = field.attributeName;
          input.value = element.value.trim();
          form.appendChild(input);
        }
      }
    });

    optionalFields.forEach((field) => {
      const element = this.querySelector(`#${field.id}`);
      if (element && element.value.trim() !== '') {
        if (!form.querySelector(`input[name="${field.attributeName}"]`)) {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = field.attributeName;
          input.value = element.value.trim();
          form.appendChild(input);
        }
      }
    });
  }

  validateOrganisationFields() {
    // Bypass validation
    return true;
    /*
    if (this.getOrganisationType() === 'individual') {
      return true;
    }

    const requiredFields = [
      { id: 'organisation-name', name: 'Organisation name' },
      { id: 'organisation-address-1', name: 'Address Line 1' },
      { id: 'organisation-town', name: 'Town or City' },
      { id: 'organisation-postcode', name: 'Postcode' },
    ];

    let isValid = true;

    requiredFields.forEach((field) => {
      const element = this.querySelector(`#${field.id}`);
      if (element) {
        if (element.value.trim() === '') {
          isValid = false;
          this.displayFieldError(element, `${field.name} is required.`);
        } else {
          this.removeFieldError(element);
        }
      }
    });

    return isValid;
    */
  }

  displayFieldError(element, message) {
    element.classList.add('error');

    // Check if error message element already exists
    let errorElement = element.nextElementSibling;
    if (!errorElement || !errorElement.classList.contains('error-message')) {
      // Create error message element
      errorElement = document.createElement('div');
      errorElement.className = 'error-message';
      element.parentNode.insertBefore(errorElement, element.nextSibling);
    }

    // Set error message text
    errorElement.textContent = message;

    // Add input event listener to remove error message when user types
    if (!element._errorInputHandler) {
      element._errorInputHandler = () => {
        this.removeFieldError(element);
      };
      element.addEventListener('input', element._errorInputHandler);
    }
  }

  removeFieldError(element) {
    element.classList.remove('error');

    // Remove error message if it exists
    let errorElement = element.nextElementSibling;
    if (errorElement && errorElement.classList.contains('error-message')) {
      errorElement.parentNode.removeChild(errorElement);
    }

    // Remove input event listener
    if (element._errorInputHandler) {
      element.removeEventListener('input', element._errorInputHandler);
      delete element._errorInputHandler;
    }
  }
  closeDialog() {
    const dialogEl = document.getElementById('favDialog');
    dialogEl && dialogEl.close();
  }

  onCheckoutClick(event) {
    event.preventDefault();
    // console.log('Gift Aid clicked');
    const consent = document.querySelector('input[name="attributes[Consent]"]');

    // Only check validity if the consent element is visible
    if (consent && this.isElementVisible(consent)) {
      consent.reportValidity();
    }

    // Directly proceed to checkout
    const checkoutBtn = document.getElementById('checkout');
    // Clear org_ localStorage values before going to checkout
    localStorage.removeItem('org_id');
    localStorage.removeItem('org_name');
    localStorage.removeItem('org_type');
    localStorage.removeItem('org_free');
    checkoutBtn.click();
  }

  isElementVisible(element) {
    // Check if element or any of its parents are hidden
    if (!element) return false;

    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden') return false;

    // Check if any parent element is hidden
    let parent = element.parentElement;
    while (parent) {
      const parentStyle = window.getComputedStyle(parent);
      if (parentStyle.display === 'none' || parentStyle.visibility === 'hidden') return false;
      parent = parent.parentElement;
    }

    return true;
  }

  async fetchDonationVariantId(rounded) {
    try {
      const response = await fetch(`/apps/donate/donate/${rounded}`, {
        method: 'POST',
      });
      const data = await response.json();
      const pattern = /^gid:\/\/shopify\/ProductVariant\/(\d+)$/;
      const match = pattern.exec(data.id);
      const id = match[1];

      return id; // Adjust based on the structure of your returned data
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';
    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId,
      );
    }
  }

  onChange(event) {
    const { value, name, checked } = event.target;
    // Gift aid dialog, changes hidden form

    if (!this.isNumber(value)) {
      return;
    }

    // Only update the quantity if the value is a number
    this.validateQuantity(event);
  }

  isNumber(num) {
    if (typeof num === 'number') {
      return num - num === 0;
    }
    if (typeof num === 'string' && num.trim() !== '') {
      return Number.isFinite ? Number.isFinite(+num) : isFinite(+num);
    }
    return false;
  }

  showDialog(event) {
    event.preventDefault();

    const dialogEl = document.getElementById('favDialog');
    if (!dialogEl) {
      console.error('Dialog element not found');
      return;
    }

    const orgFields = dialogEl.querySelector('#organisation-details-section');
    const individualFields = dialogEl.querySelector('#gift-aid-section');

    if (this.getOrganisationType() !== 'individual') {
      orgFields.style.display = 'block';
      individualFields.style.display = 'none';
    } else {
      orgFields.style.display = 'none';
      individualFields.style.display = 'block';
    }

    dialogEl.showModal();
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'donation-cart',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents-donation',
      },
    ];
  }
}

customElements.define('cart-donations', CartDonations);
